// Demolition & Strip-out — per-template authored content. See ./types.ts for the content rule.
import type { TemplateSpecific } from "./types";

export const DEMOLITION_STRIP: Record<string, TemplateSpecific> = {
  "demo-soft-strip": {
    whoFor:
      "This soft strip method statement is for demolition and strip-out contractors taking a building down to its frame before structural works begin. If your principal contractor or client has asked for a RAMS covering the hand removal of fixtures, fittings, finishes and services, this is the document they expect to see. It is written for the operatives actually pulling out kitchens, partitions, ceilings and cabling, and for the supervisor who has to demonstrate the work is planned and controlled.",
    scope:
      "The works covered are the manual soft strip of a building's non-structural elements: doors, partitions, suspended ceilings, floor finishes, sanitaryware, joinery, redundant cabling and pipework, ahead of any structural demolition. Strip-out is by hand and with small power tools only; no machine demolition, no removal of load-bearing elements and no work on the primary structure is included. Asbestos removal is explicitly excluded — if a suspected asbestos-containing material is found, work stops and the discovery procedure is followed. Live services isolation and the structural demolition phase are covered by separate RAMS.",
    legislation: [
      "Construction (Design and Management) Regulations 2015 — reg 20 demolition arrangements",
      "Control of Asbestos Regulations 2012 — regs 5 and 11 (duty to manage, emergency procedure)",
      "HSG264 Asbestos: The survey guide",
      "Manual Handling Operations Regulations 1992 (as amended)",
      "Control of Substances Hazardous to Health Regulations 2002 (COSHH)",
      "Environmental Protection Act 1990 — duty of care for waste",
    ],
    sequence: [
      "Review the refurbishment and demolition (R&D) asbestos survey and the as-built drawings before mobilising; confirm the strip scope and any retained elements with the principal contractor.",
      "Confirm in writing that electrical, gas and water services to the strip area have been isolated and proved dead; treat any unidentified service as live until proven otherwise.",
      "Set up the waste route — protected floor chute or hoist position, banksman, and segregated skips for timber, metal, plasterboard and general waste under the waste duty of care.",
      "Strip from the top down and from the centre of rooms outward: ceilings and high-level services first, then partitions and joinery, then floor finishes, keeping retained structure protected throughout.",
      "Carry or barrow stripped materials to the chute or hoist in manageable loads; never overfill chutes and keep the drop zone barriered.",
      "On discovery of any suspected asbestos-containing material, stop work immediately, leave the area, prevent access and notify the principal contractor before sampling or removal is arranged.",
      "Clean down progressively, remove waste under transfer notes, and hand back the stripped area for the structural phase with a written confirmation of completion.",
    ],
    hazards: [
      {
        hazard: "Contact with concealed live services (electrical, gas, water) behind finishes",
        who: ["Operatives", "Supervisor"],
        initial: { likelihood: 4, severity: 5 },
        controls: [
          "Obtain written isolation and proving-dead confirmation for all services before stripping begins",
          "Use a CAT and genny / cable detector before cutting into walls, floors or ceiling voids",
          "Treat every unidentified cable or pipe as live until positively proven dead",
          "Stop and report any service not shown on drawings before proceeding",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
      {
        hazard: "Accidental damage to retained structure or adjacent finishes during hand strip",
        who: ["Operatives", "Following trades"],
        initial: { likelihood: 3, severity: 3 },
        controls: [
          "Clearly mark and protect all retained elements on drawings and on site before work starts",
          "Strip in the planned top-down sequence so loads are not transferred onto retained elements",
          "Use hand tools and small power tools only — no machine or percussive demolition in the strip phase",
          "Brief operatives on the line between strip-out scope and structural elements",
        ],
        residual: { likelihood: 2, severity: 2 },
      },
      {
        hazard: "Sharps, needles and biohazards in derelict or void buildings",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 3 },
        controls: [
          "Carry out a pre-entry inspection for needles, faeces and sharps before operatives enter",
          "Issue cut-resistant gloves and never put hands into voids or rubbish blind",
          "Provide a sharps bin and a documented needlestick first-aid and reporting procedure",
        ],
        residual: { likelihood: 2, severity: 2 },
      },
      {
        hazard: "Repetitive manual handling carrying stripped materials to chutes and skips",
        who: ["Operatives"],
        initial: { likelihood: 4, severity: 3 },
        controls: [
          "Position the waste chute or hoist to minimise carrying distance from the work face",
          "Use wheelbarrows, sack trucks or material hoists to mechanise repeated movement",
          "Keep individual loads light and rotate operatives on carrying tasks",
          "Brief operatives on safe lifting technique and a no-overfill rule for chutes",
        ],
        residual: { likelihood: 2, severity: 2 },
      },
      {
        hazard: "Unexpected discovery of asbestos-containing material during strip-out",
        who: ["Operatives", "Other site users"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Review the R&D survey before work and brief operatives on what ACMs look like in situ",
          "Stop work, withdraw and prevent access on any suspected discovery — do not disturb further",
          "Notify the principal contractor and arrange a licensed survey or removal before resuming",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
    ],
    ppe: [
      "Safety helmet",
      "Cut-resistant gloves",
      "Safety boots with midsole protection",
      "Eye protection",
      "FFP3 disposable mask for dusty tasks",
      "High-visibility clothing",
    ],
    competence: [
      "CCDO or NDTG demolition training appropriate to soft strip",
      "Asbestos awareness training (UKATA or IATP) refreshed annually",
      "Manual handling training",
      "Site-specific induction and toolbox talk on the strip sequence",
    ],
    permits: [
      "Permit to work confirming service isolation before strip begins",
      "Hot works permit where cutting or grinding is required",
    ],
    equipment: [
      "Hand tools — wrecking bars, claw hammers, bolt croppers",
      "Cordless reciprocating saws and small power tools",
      "CAT and genny cable / service detector",
      "Protected waste chute or material hoist",
      "Wheelbarrows and sack trucks",
      "Segregated waste skips with transfer documentation",
    ],
    pcChecks: [
      "The discovery procedure is named — reviewers will not accept a RAMS that has operatives carrying on if asbestos is suspected.",
      "Service isolation is stated as written and proved-dead before strip, not 'services believed isolated'.",
      "The strip sequence protects identified retained structure and keeps the work clear of any structural demolition scope.",
    ],
    faqs: [
      {
        q: "Do I need an asbestos survey before soft strip-out?",
        a: "Yes. Before any strip-out or demolition you need a refurbishment and demolition (R&D) survey carried out to HSG264, not just a management survey. A management survey is only for normal occupation and does not look behind finishes or into the fabric you are about to disturb. The R&D survey is intrusive and is designed to find ACMs in exactly the places soft strip exposes. Without it your RAMS cannot reasonably control the asbestos risk and a principal contractor will reject it.",
      },
      {
        q: "Can we use a machine for soft strip to speed it up?",
        a: "No — soft strip by definition is the hand removal of non-structural fixtures, finishes and services before any machine demolition. Bringing an excavator or breaker into the strip phase changes the risk profile completely and is covered by a separate structural demolition RAMS. Machine work also makes it far harder to spot and stop on a suspected asbestos discovery. Keep the soft strip to hand tools and small power tools, and only move to mechanical demolition once the building is stripped and the structural method statement is in place.",
      },
    ],
  },

  "demo-structural": {
    whoFor:
      "Structural demolition is high-hazard work and this demolition method statement is the document a principal contractor and the HSE expect before a structure comes down. It is aimed at the demolition contractor's management, the appointed person directing the demolition and the plant operators carrying it out. If you are pulling down load-bearing structure adjacent to occupied buildings, roads or services, this RAMS sits alongside the BS 6187 demolition sequence designed for the specific structure.",
    scope:
      "The works covered are the planned demolition of load-bearing structure — frames, floors, walls and roofs — by mechanical and, where specified, pre-weakening methods, following a sequence designed by a competent temporary works engineer to BS 6187:2011. The scope includes exclusion-zone management, dust suppression, and protection of adjacent property and party walls. It does not cover the preceding soft strip phase, asbestos removal, or any structural alteration falling outside the agreed demolition design. All services must be disconnected and certificated before any structural work starts; nothing in this RAMS authorises work on live services.",
    legislation: [
      "Construction (Design and Management) Regulations 2015 — reg 20 written demolition plan",
      "BS 6187:2011 Code of practice for full and partial demolition",
      "Work at Height Regulations 2005",
      "HSG47 Avoiding danger from underground services",
      "Provision and Use of Work Equipment Regulations 1998 (PUWER)",
      "Party Wall etc. Act 1996",
    ],
    sequence: [
      "Obtain the BS 6187 demolition sequence and structural survey from the temporary works engineer and agree the order of removal, pre-weakening and any temporary support.",
      "Confirm and file the service disconnection certificates for electricity, gas, water and telecoms before any plant is mobilised onto the structure.",
      "Erect adjacent-property and party-wall protection, sheeting and hoardings, and serve any notices required under the Party Wall Act.",
      "Establish the machine exclusion zone sized to the structure height and reach, with a banksman, edge protection and a marshalled plant route.",
      "Carry out pre-weakening only as specified in the engineered sequence, keeping operatives clear and the structure stable at every stage.",
      "Demolish progressively top-down or by the engineered method, running fire-hose or misting dust suppression continuously and monitoring the structure for unplanned movement.",
      "Process and remove arisings, then survey the remaining structure and ground before handing over or moving to the next phase.",
    ],
    hazards: [
      {
        hazard: "Premature or uncontrolled collapse of the structure",
        who: ["Plant operators", "Operatives", "Public", "Adjacent occupiers"],
        initial: { likelihood: 4, severity: 5 },
        controls: [
          "Demolish strictly to the BS 6187 sequence designed by a temporary works engineer",
          "Apply pre-weakening and temporary support only as engineered, with no deviation on site",
          "Monitor the structure continuously for unplanned movement and stop on any sign of instability",
          "Keep all personnel outside the exclusion zone during active demolition",
        ],
        residual: { likelihood: 2, severity: 5 },
      },
      {
        hazard: "Person struck by demolition plant or falling debris inside the exclusion zone",
        who: ["Operatives", "Banksman", "Plant operators"],
        initial: { likelihood: 4, severity: 5 },
        controls: [
          "Size the exclusion zone to structure height plus machine reach and barrier it physically",
          "Enforce a no-entry rule during demolition with a dedicated banksman and agreed signals",
          "Separate pedestrian and plant routes and keep operatives out of the swing radius",
          "Use machine cameras and proximity controls where fitted",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
      {
        hazard: "Damage to or collapse of adjacent property and party walls",
        who: ["Adjacent occupiers", "Public", "Operatives"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Install engineered protection and temporary support to party walls before demolition starts",
          "Serve Party Wall Act notices and agree a condition schedule with the adjoining owner",
          "Demolish in the engineered sequence to avoid loading the retained party structure",
          "Monitor adjacent structures for movement throughout the works",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
      {
        hazard: "Silica and demolition dust drifting beyond the site",
        who: ["Operatives", "Public", "Adjacent occupiers"],
        initial: { likelihood: 4, severity: 4 },
        controls: [
          "Run continuous dust suppression with fire hoses and misting cannons during demolition",
          "Damp down arisings and stockpiles and avoid dropping material from height dry",
          "Monitor wind and stop or rephase work when suppression cannot control drift",
          "Provide FFP3 respiratory protection to operatives in the dust zone",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard: "Contact with services believed dead but not certificated as disconnected",
        who: ["Plant operators", "Operatives"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Hold signed service disconnection certificates for every supply before structural work starts",
          "Trace and prove buried services with detection equipment per HSG47 before ground disturbance",
          "Do not accept verbal or assumed isolation — no certificate means no work in that area",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
    ],
    ppe: [
      "Safety helmet with chin strap",
      "Impact-rated eye protection",
      "FFP3 respiratory protection",
      "Hearing protection",
      "High-visibility clothing",
      "Safety boots with midsole protection",
    ],
    competence: [
      "CCDO supervisor / NDTG demolition supervisor competence",
      "Plant operator CPCS or equivalent for the demolition machine in use",
      "Appointed person for demolition directing the works",
      "Temporary works engineer competent in BS 6187 sequencing",
    ],
    permits: [
      "Permit to demolish issued against the agreed BS 6187 sequence",
      "Service disconnection certificates filed before commencement",
      "Permit for any work at height on partially demolished structure",
    ],
    equipment: [
      "High-reach demolition excavator with selector grab and shear",
      "360 tracked excavator with breaker attachment",
      "Misting cannons and fire-hose dust suppression",
      "Temporary propping and party-wall support systems",
      "Site hoarding, debris netting and scaffold protection fans",
      "Structural monitoring targets and survey instruments",
    ],
    pcChecks: [
      "Service disconnection certificates are referenced — reviewers will not accept 'services believed dead'.",
      "The demolition sequence cites a named BS 6187 design by a temporary works engineer, not a generic top-down statement.",
      "Exclusion-zone dimensions are tied to the actual structure height and machine reach, with adjacent-property protection detailed.",
    ],
    faqs: [
      {
        q: "Does structural demolition legally need a written method statement?",
        a: "Yes. Under CDM 2015 regulation 20, where construction work involves demolition or dismantling that could endanger anyone, the arrangements must be planned and recorded in writing before the work begins. In practice that means a demolition plan supported by a method statement and risk assessment, with the sequence designed to BS 6187:2011. The plan must address how the structure will be brought down safely and how everyone on and around the site is protected. A principal contractor cannot let structural demolition start without it.",
      },
      {
        q: "How big should the demolition exclusion zone be?",
        a: "There is no single fixed figure — the zone is engineered for the specific structure and method. As a working basis it is sized to the height of the structure being demolished plus the reach of the machine, then adjusted for debris throw, ground conditions and any pre-weakening. It must be physically barriered, controlled by a banksman and kept clear of all personnel during active demolition. The BS 6187 sequence and the temporary works design should state the zone for each phase, and your RAMS should reproduce those figures rather than a generic distance.",
      },
    ],
  },

  "demo-asbestos-awareness": {
    whoFor:
      "Anyone whose work could disturb the fabric of a pre-2000 building may encounter asbestos, and this asbestos awareness risk assessment is for those workers — strip-out crews, maintenance trades, refurbishment contractors — not for licensed removal teams. Its purpose is to make sure operatives can recognise asbestos-containing materials in situ and know exactly what to do if they find them. Principal contractors and clients ask for it to evidence that everyone on a refurbishment or demolition site has been informed, instructed and trained to awareness level before work starts.",
    scope:
      "This document covers working in or around buildings where asbestos-containing materials may be present, at asbestos awareness level only. It sets out how to recognise likely ACMs, how to use the R&D survey, and the emergency procedure to follow on a suspected or accidental discovery. It is explicitly not a removal RAMS: licensed asbestos work and notifiable non-licensed work (NNLW) are excluded and must be carried out by a competent licensed or notified contractor under separate documentation. No deliberate disturbance, sampling or removal of asbestos is authorised by this RAMS — the only action on discovery is to stop, withdraw and report.",
    legislation: [
      "Control of Asbestos Regulations 2012 — reg 10 (information, instruction and training) and reg 11 (emergency procedures)",
      "Control of Asbestos Regulations 2012 — regs 5 to 7 (duty to manage, survey and assessment)",
      "L143 Managing and working with asbestos (ACOP and guidance)",
      "HSG264 Asbestos: The survey guide",
      "Construction (Design and Management) Regulations 2015 — reg 20",
      "Health and Safety at Work etc. Act 1974",
    ],
    sequence: [
      "Before mobilising, obtain and read the refurbishment and demolition (R&D) survey and asbestos register for the building, and identify where ACMs are recorded.",
      "Brief all operatives at awareness level on what asbestos looks like in situ — insulation board, textured coatings, cement sheets, lagging, floor tiles and gaskets.",
      "Walk the work area against the survey and agree which locations are flagged, presumed or to be treated as containing asbestos.",
      "Confirm the stop-work and reporting procedure with every operative: who to tell, how to seal the area and how to prevent access.",
      "Carry out the planned non-asbestos work, staying alert for unsurveyed materials disturbed during the task.",
      "On any suspected or accidental discovery, stop work, leave the area, prevent further disturbance and access, and report immediately under CAR 2012 reg 11.",
      "Hold work in that area until a licensed survey, sampling or removal has been arranged and the material is confirmed safe or cleared.",
    ],
    hazards: [
      {
        hazard: "Inhalation of fibres from unknowingly disturbing asbestos-containing material",
        who: ["Operatives", "Other trades on site"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Read the R&D survey and asbestos register before any work that disturbs the fabric",
          "Train operatives to awareness level so they recognise likely ACMs before cutting or breaking",
          "Stop and treat any unsurveyed suspect material as asbestos until proven otherwise",
          "Provide FFP3 protection for incidental dust tasks as a last line of defence",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Spread of contamination after an accidental discovery",
        who: ["Operatives", "Other site users", "Cleaners"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Stop work immediately and do not disturb the material further",
          "Withdraw, seal off the area and prevent access until it is assessed",
          "Avoid walking debris through the site or removing potentially contaminated clothing yourself",
          "Report to the principal contractor so a licensed assessment can be arranged",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Failure to recognise an asbestos-containing material before disturbing it",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Deliver UKATA or IATP asbestos awareness training, refreshed annually",
          "Brief on the specific ACM types recorded in this building's survey",
          "Provide reference images and on-site examples of likely materials during induction",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
      {
        hazard: "Operatives carrying out removal that should be licensed or notifiable (NNLW)",
        who: ["Operatives", "Supervisor"],
        initial: { likelihood: 2, severity: 5 },
        controls: [
          "State clearly that this RAMS authorises no asbestos removal, sampling or disturbance",
          "Route all licensed work and NNLW to a competent licensed or notified contractor",
          "Reinforce that awareness training does not permit any work on asbestos",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
    ],
    ppe: [
      "FFP3 disposable respirator for incidental dust exposure",
      "Disposable Type 5 coveralls where dusty work is foreseeable",
      "Safety boots without laces (easier to decontaminate)",
      "Disposable gloves",
      "Safety helmet",
      "High-visibility clothing",
    ],
    competence: [
      "UKATA or IATP asbestos awareness training, refreshed annually",
      "Site induction covering this building's asbestos register",
      "Supervisor briefed on the CAR 2012 reg 11 emergency procedure",
    ],
    permits: [
      "No permit authorises asbestos disturbance under this awareness-level RAMS",
      "Permit to work for the underlying non-asbestos task only",
    ],
    equipment: [
      "Refurbishment and demolition (R&D) survey and asbestos register",
      "ACM identification reference guide and photographs",
      "Warning signage and barrier tape to seal a discovery",
      "Polythene sheeting to cover a disturbed material",
      "Asbestos discovery report form and incident log",
    ],
    pcChecks: [
      "The RAMS explicitly excludes licensed work and NNLW — reviewers reject any awareness document that implies operatives may remove asbestos.",
      "An R&D survey to HSG264 is referenced, not a management survey, for refurbishment or demolition work.",
      "The reg 11 emergency procedure names who stops work, how the area is sealed and who is told.",
    ],
    faqs: [
      {
        q: "Does asbestos awareness training let me remove asbestos?",
        a: "No. Asbestos awareness training under CAR 2012 regulation 10 is information and instruction only — it teaches you to recognise asbestos and to stop and report if you find it. It does not qualify you to remove, sample or otherwise disturb any asbestos-containing material. Licensed work and notifiable non-licensed work (NNLW) must be carried out by a licensed or notified contractor with the right training, equipment and notification. If you find suspected asbestos, the only correct action under this RAMS is to stop, withdraw and report.",
      },
      {
        q: "What do I do if I find suspected asbestos during the works?",
        a: "Follow the regulation 11 emergency procedure: stop work straight away, do not disturb the material any further, and leave the area. Seal off and prevent access so no one else walks into it or spreads contamination, and report the discovery to your supervisor and the principal contractor immediately. Work in that location stays on hold until a licensed survey or sampling confirms whether it is asbestos and, if so, a licensed contractor deals with it. You should never try to clean it up, bag it or carry on around it yourself.",
      },
    ],
  },
};
